/**
 * Play HUD telemetry plate — direction rose, speed dial, wind arrow.
 * The dial is 150 km/h, or 300 km/h while Headreach is the ring mode.
 * The workshop pond docks the same plate under its boat picture.
 * Scale is a separate bottom-left bar. See docs/PLAY-HUD.md. Not tool chrome.
 */
import type { JSX } from 'preact';
import { setPanel } from '../panels.ts';
import { HEADREACH_TOP_KMH } from '../../sim/boat/headreach.ts';
import type { RingMode } from '../../sim/replay.ts';
import './telemetryStrip.css';

export type TelemetryStripState = {
  speedKmh: number;
  bearing: number;
  windDirectionDeg: number;
  windSpeed: number;
  /** Which ring meter to draw. Off draws none. */
  ringMode: RingMode;
  /** 0–1 fill for the selected meter. Ignored when the ring is off. */
  meterLevel: number;
};

export type TelemetryStripProps = {
  state: TelemetryStripState;
};

export type TelemetryStrip = {
  sync: (state: TelemetryStripState) => void;
  destroy: () => void;
};

const INITIAL_STATE: TelemetryStripState = {
  speedKmh: 0,
  bearing: 0,
  windDirectionDeg: 0,
  windSpeed: 0,
  ringMode: 'off',
  meterLevel: 0,
};

/** Semicircle on the baseline: left (180°) over the top to right (360°). */
const SPEED_DIAL_MAX_KMH = 150;

function dialMaxKmh(ringMode: RingMode): number {
  return ringMode === 'headreach' ? HEADREACH_TOP_KMH : SPEED_DIAL_MAX_KMH;
}

const REDLINE_T = 0.8;
const DIAL_START_DEG = 180;
const DIAL_SWEEP_DEG = 180;
const DIAL_CX = 120;
const DIAL_CY = 118;
const DIAL_R = 96;
const NEEDLE_TIP_R = DIAL_R - 22;
const NEEDLE_HALF_W = 7;
const TICK_COUNT = 40;
const MAJOR_EVERY = 10;
const MID_EVERY = 5;
const VIEW_W = 240;
const VIEW_H = 136;
const TICK_INNER_MAJOR = DIAL_R - 17;
const TICK_INNER_MID = DIAL_R - 12;
const TICK_INNER_MINOR = DIAL_R - 7;

function formatBearing(bearing: number): string {
  return `${String(Math.round(bearing) % 360).padStart(3, '0')}°`;
}

function compassPoint(degrees: number): string {
  const points = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const;
  const index =
    Math.round((((degrees % 360) + 360) % 360) / 45) % points.length;
  return points[index] ?? 'N';
}

function wrapHeading(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

function polar(cx: number, cy: number, radius: number, deg: number): { x: number; y: number } {
  const rad = (deg * Math.PI) / 180;
  return { x: cx + Math.cos(rad) * radius, y: cy + Math.sin(rad) * radius };
}

function sweepDeg(t: number): number {
  return DIAL_START_DEG + DIAL_SWEEP_DEG * Math.min(1, Math.max(0, t));
}

function sweepPoint(t: number, radius: number): { x: number; y: number } {
  return polar(DIAL_CX, DIAL_CY, radius, sweepDeg(t));
}

function arcPath(radius: number, t0: number, t1: number): string {
  const start = sweepPoint(t0, radius);
  const end = sweepPoint(t1, radius);
  const sweep = DIAL_SWEEP_DEG * Math.min(1, Math.max(0, t1 - t0));
  const large = sweep > 180 ? 1 : 0;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 ${large} 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}

function formatWindSpeed(speedMs: number): string {
  return `${speedMs.toFixed(1)} m/s`;
}

function windAriaLabel(heading: number, speedMs: number): string {
  if (speedMs <= 0) {
    return 'Wind calm';
  }
  return `Wind ${compassPoint(heading)} ${formatWindSpeed(speedMs)}`;
}

function needlePoints(t: number): string {
  const tip = sweepPoint(t, NEEDLE_TIP_R);
  const nx = tip.x - DIAL_CX;
  const ny = tip.y - DIAL_CY;
  const len = Math.hypot(nx, ny) || 1;
  const ux = nx / len;
  const uy = ny / len;
  const px = -uy * NEEDLE_HALF_W;
  const py = ux * NEEDLE_HALF_W;
  const bx = DIAL_CX - ux * 3;
  const by = DIAL_CY - uy * 3;
  return `${tip.x.toFixed(2)},${tip.y.toFixed(2)} ${(bx + px).toFixed(2)},${(by + py).toFixed(2)} ${(bx - px).toFixed(2)},${(by - py).toFixed(2)}`;
}

function ReachMeter({
  label,
  fullLabel,
  level,
}: {
  label: string;
  fullLabel: string;
  level: number;
}): JSX.Element {
  const fill = Math.min(1, Math.max(0, level));
  const full = fill >= 1;
  const percent = Math.round(fill * 100);

  return (
    <div
      class={`play-telemetry-plane${full ? ' is-full' : ''}`}
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      aria-label={full ? fullLabel : `${label} ${percent} percent`}
    >
      <span class="play-telemetry-label">{label}</span>
      <span class="play-telemetry-plane-track">
        <span class="play-telemetry-plane-fill" style={{ width: `${percent}%` }} />
      </span>
    </div>
  );
}

export function SpeedDial({
  speedKmh,
  ringMode,
  meterLevel,
}: {
  speedKmh: number;
  ringMode: RingMode;
  meterLevel: number;
}): JSX.Element {
  const fill = Math.min(1, Math.max(0, speedKmh / dialMaxKmh(ringMode)));
  const inRedline = fill >= REDLINE_T;
  const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, index) => {
    const t = index / TICK_COUNT;
    const major = index % MAJOR_EVERY === 0;
    const mid = !major && index % MID_EVERY === 0;
    const outer = sweepPoint(t, DIAL_R);
    const inner = sweepPoint(t, major ? TICK_INNER_MAJOR : mid ? TICK_INNER_MID : TICK_INNER_MINOR);
    const redline = t >= REDLINE_T;
    const kind = major ? 'major' : mid ? 'mid' : 'minor';
    return { index, outer, inner, kind, redline };
  });
  const readout = `${Math.round(speedKmh)} km/h`;

  return (
    <div
      class="play-telemetry-dial"
      data-cell="speed"
      aria-label={`Speed ${Math.round(speedKmh)} kilometres per hour`}
    >
      <svg
        class="play-telemetry-dial-svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        aria-hidden="true"
      >
        <path class="play-telemetry-dial-rail" d={arcPath(TICK_INNER_MAJOR, 0, REDLINE_T)} />
        <path class="play-telemetry-dial-redline" d={arcPath(TICK_INNER_MAJOR, REDLINE_T, 1)} />
        {ticks.map((tick) => (
          <line
            key={tick.index}
            class={`play-telemetry-dial-tick is-${tick.kind}${tick.redline ? ' is-redline' : ''}`}
            x1={tick.inner.x}
            y1={tick.inner.y}
            x2={tick.outer.x}
            y2={tick.outer.y}
          />
        ))}
        <polygon class="play-telemetry-dial-needle" points={needlePoints(fill)} />
        <circle class="play-telemetry-dial-hub-ring" cx={DIAL_CX} cy={DIAL_CY} r="9" />
        <circle class="play-telemetry-dial-hub" cx={DIAL_CX} cy={DIAL_CY} r="3.2" />
      </svg>
      <div class="play-telemetry-dial-readout">
        <span class="play-telemetry-label">Speed</span>
        <span class="play-telemetry-value">
          <span class={`play-telemetry-speed-num${inRedline ? ' is-redline' : ''}`}>
            {readout}
          </span>
        </span>
        {ringMode === 'planing' ? (
          <ReachMeter label="Plane" fullLabel="Planing" level={meterLevel} />
        ) : null}
        {ringMode === 'headreach' ? (
          <ReachMeter label="Headreach" fullLabel="Headreach" level={meterLevel} />
        ) : null}
      </div>
    </div>
  );
}

export function BearingCompass({
  bearing,
  label = 'Direction',
}: {
  bearing: number;
  label?: string;
}): JSX.Element {
  const heading = wrapHeading(bearing);
  const degrees = formatBearing(heading);

  return (
    <div class="play-telemetry-compass" data-cell="bearing" aria-label={`${label} ${degrees}`}>
      <svg class="play-telemetry-compass-svg" viewBox="0 0 64 64" aria-hidden="true">
        <polygon class="play-telemetry-compass-cardinal is-north" points="32,4 35.2,32 32,28.4 28.8,32" />
        <polygon class="play-telemetry-compass-cardinal" points="60,32 32,35.2 35.6,32 32,28.8" />
        <polygon class="play-telemetry-compass-cardinal" points="32,60 28.8,32 32,35.6 35.2,32" />
        <polygon class="play-telemetry-compass-cardinal" points="4,32 32,28.8 28.4,32 32,35.2" />
        <polygon class="play-telemetry-compass-ordinal" points="48.8,15.2 33.4,32 32,29.8 29.8,32" />
        <polygon class="play-telemetry-compass-ordinal" points="48.8,48.8 32,33.4 34.2,32 32,29.8" />
        <polygon class="play-telemetry-compass-ordinal" points="15.2,48.8 30.6,32 32,34.2 34.2,32" />
        <polygon class="play-telemetry-compass-ordinal" points="15.2,15.2 32,30.6 29.8,32 32,34.2" />
        <text class="play-telemetry-compass-n" x="32" y="15">
          N
        </text>
        <polygon
          class="play-telemetry-compass-needle"
          points="32,6 36.6,36 32,32 27.4,36"
          transform={`rotate(${heading} 32 32)`}
        />
        <circle class="play-telemetry-compass-hub" cx="32" cy="32" r="2.8" />
      </svg>
      <div class="play-telemetry-dial-readout">
        <span class="play-telemetry-label">{label}</span>
        <span class="play-telemetry-value">{degrees}</span>
      </div>
    </div>
  );
}

function WindArrow({ heading }: { heading: number }): JSX.Element {
  return (
    <svg class="play-telemetry-wind-icon" viewBox="0 0 64 64" aria-hidden="true">
      <polygon
        class="play-telemetry-wind-arrow"
        points="32,8 48,56 32,46 16,56"
        transform={`rotate(${heading} 32 32)`}
      />
      <circle class="play-telemetry-wind-hub" cx="32" cy="32" r="3.2" />
    </svg>
  );
}

export function WindMeter({
  directionDeg,
  speed,
}: {
  directionDeg: number;
  speed: number;
}): JSX.Element {
  const speedMs = speed;
  const heading = wrapHeading(directionDeg);
  const readout = formatWindSpeed(speedMs);

  return (
    <div
      class="play-telemetry-wind"
      data-cell="wind"
      data-heading={heading.toFixed(1)}
      aria-label={windAriaLabel(heading, speedMs)}
    >
      <WindArrow heading={heading} />
      <div class="play-telemetry-dial-readout">
        <span class="play-telemetry-label">Wind</span>
        <span class="play-telemetry-value">{readout}</span>
      </div>
    </div>
  );
}

/** The plate itself. `race` docks bottom-right; `pond` sits in the workshop column. */
export function TelemetryHud({
  state,
  placement = 'race',
}: {
  state: TelemetryStripState;
  placement?: 'race' | 'pond';
}): JSX.Element {
  return (
    <aside
      class={`play-telemetry play-instruments${placement === 'pond' ? ' is-pond' : ''}`}
      aria-label="Telemetry"
    >
      <BearingCompass bearing={state.bearing} />
      <SpeedDial
        speedKmh={state.speedKmh}
        ringMode={state.ringMode}
        meterLevel={state.meterLevel}
      />
      <WindMeter directionDeg={state.windDirectionDeg} speed={state.windSpeed} />
    </aside>
  );
}

export function TelemetryStripPanel({ state }: TelemetryStripProps): JSX.Element {
  return <TelemetryHud state={state} placement="race" />;
}

export function mountTelemetryStrip(): TelemetryStrip {
  setPanel('telemetry', { state: INITIAL_STATE });

  return {
    sync: (state) => setPanel('telemetry', { state }),
    destroy: () => setPanel('telemetry', null),
  };
}
